/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../lib/@waca/core-client/js/core-client/ui/core/View',
	'underscore',
	'jquery',
	'text!./templates/NavigationView.html',
	'../nls/StringResources'
], function(BaseView, _, $, Template, stringResources) {

	var View = BaseView.extend({

		templateString: Template,
		items: null,
		currentPosition: -1,
		callbacks: null,
		rendered: false,

		pendingRender: Promise.resolve(),
		pendingClear: Promise.resolve(),

		events: {
			'click .navigationBar .clickable': 'onNavigationClick'
		},

		init: function(options) {
			View.inherited('init', this, arguments);
			this.callbacks = options.callbacks;
			this.segue = options.segue;
			this.items = [];
		},

		_animationFactor: function() {
			return 1;
		},

		render: function() {

			var html = this.dotTemplate({});
			this.$el.html(html);

			this.rendered = true;

			let promise;
			if(this.items.length) {
				var item = this.items[this.currentPosition];
				promise = this.displayItem(item);
			} else {
				promise = Promise.resolve();
			}

			return promise;
		},

		remove: function() {
			View.inherited('remove', this, arguments);

			this.rendered = false;
		},

		getCount: function() {
			return this.items.length;
		},

		updateTitle: function() {
			var canGoBack = false;

			var title = null;
			if(this.currentPosition >= 0) {
				var item = this.items[this.currentPosition];
				if(item.getTitle) {
					title = item.getTitle();
				}
			}

			if(this.currentPosition > 0 && title === '') {
				title = stringResources.get('navigationBack');
			}

			canGoBack = this.currentPosition > 0;

			var navigationBarLink = this.$el.find('.navigationBar').toggle(title ? true : false).find('> div');
			navigationBarLink.find('.title').html(title);
			navigationBarLink.toggleClass('clickable', canGoBack);
		},

		cleanItem: function(item) {
			let promise;
			if(item) {
				promise = new Promise((resolve, reject) => {
					try {
						item.$el.animate({
							'opacity': 0
						}, 300 * this._animationFactor(), () => {
							item.remove();
							resolve();
						});
					} catch(error) {
						reject(error);
					}
				});
			} else {
				promise = Promise.resolve();
			}
			return promise;
		},

		renderItem: function(item, currentItem) {

			var container = $('<div />');
			container.appendTo(this.$el.find('.navigationPanel'));

			let promises = [];

			item.$el = container;
			const rendered = item.render();
			if(rendered && rendered.then) {
				// We don't know what promise item.render() returns.
				// For safety, wrap it in a `Promise.resolve()`
				// TODO: get rid of the wrapper
				promises.push(Promise.resolve().then(rendered));
			}

			item.$el.css('opacity', 0);

			const promise = new Promise((resolve, reject) => {
				try {
					item.$el.animate({
						opacity: 1
					}, 300 * this._animationFactor(), resolve);
				} catch(error) {
					reject(error);
				}
			});
			promises.push(promise);

			if(currentItem) {
				currentItem.$el.animate({
					opacity: 0
				}, 300 * this._animationFactor());
			}

			return Promise.all(promises);
		},

		displayItem: function(item, currentItem) {
			this.updateTitle();
			return this.renderItem(item, currentItem);
		},

		pushTop: function(item) {

			var currentItem = this.items[this.currentPosition];

			this.items = [item];
			this.currentPosition = 0;

			let promise;
			if(this.rendered) {
				// Prevent any pointer events during the animation and render phase.
				this.$el.css('pointer-events', 'none');

				// Another call to render the item is executing, wait for it to complete, and then push the item.
				promise = this.pendingRender = this.pendingRender
					.then(this.pushTopItem.bind(this, item, currentItem));
			} else {
				promise = this.pendingRender;
			}

			return promise;
		},

		pushTopItem: function(item, currentItem) {
			return this.displayItem(item, currentItem)
				.then(() => {
					if(currentItem) {
						currentItem.remove();
					}

					// Renable pointer events. The animation and render phase is complete.
					this.$el.css('pointer-events', 'auto');
				});
		},

		push: function(item) {

			this.items.push(item);
			this.currentPosition++;

			let promise;
			if(this.rendered) {
				// Prevent any pointer events during the animation and render phase.
				this.$el.css('pointer-events', 'none');

				var currentItem = this.items[this.currentPosition - 1];

				// Another call to render the item is executing, wait for it to complete, and then push the item.
				promise = this.pendingRender = this.pendingRender
					.then(this.pushItem.bind(this, item, currentItem));
			} else {
				promise = this.pendingRender;
			}

			return promise;
		},

		pushItem: function(item, currentItem) {
			return this.displayItem(item, currentItem)
				.then(() => {
					if(currentItem) {
						currentItem.hide();
					}

					// Renable pointer events. The animation and render phase is complete.
					this.$el.css('pointer-events', 'auto');
				});
		},

		pop: function() {
			var currentItem = this.items.pop();
			if(currentItem) {
				this.currentPosition--;
			}

			let promise;
			if(this.rendered) {
				// Prevent any pointer events during the animation and render phase.
				this.$el.css('pointer-events', 'none');

				var item = _.last(this.items);

				// Another call to render the item is executing, wait for it to complete, and then push the item.
				promise = this.pendingRender = this.pendingRender
					.then(this.popItem.bind(this, item, currentItem));
			} else {
				promise = this.pendingRender;
			}

			return promise;
		},

		popItem: function(item, currentItem) {

			if(item) {
				item.$el.show();
				item.$el.css('opacity', 0);
				item.$el.animate({
					opacity: 1
				}, 300 * this._animationFactor(), function() {});
			}

			return this.cleanItem(currentItem)
				.then(() => {
					this.updateTitle();

					if(this.callbacks && this.callbacks.onPop) {
						this.callbacks.onPop();
					}

					// Renable pointer events. The animation and render phase is complete.
					this.$el.css('pointer-events', 'auto');
				});
		},

		_clear: function() {
			if(this.items.length > 0) {

				var currentItem = _.last(this.items);

				// Clear the array.
				this.items = [];
				this.currentPosition = -1;

				if(this.rendered) {
					this.pendingClear = this.pendingClear
						.then(this.cleanItem.bind(this, currentItem));
				} else {
					this.pendingClear = Promise.resolve();
				}
			}

			return this.pendingClear;
		},

		onNavigationClick: function() {
			return this.pop();
		}
	});
	return View;
});
